import React from 'react';
import Text from '@zg-rentals/multibrand-ui/lib/Text';
import Select from '@zg-rentals/multibrand-ui/lib/Select';
import Button from '@zg-rentals/multibrand-ui/lib/Button';
import Label from '@zg-rentals/multibrand-ui/lib/Label';
import SpacerRow from '@zg-rentals/multibrand-ui/lib/SpacerRow';
import Container from '@zg-rentals/multibrand-ui/lib/Container';
import Section from '@rental-manager/components/Section';
import Modal from '@common/components/Modal';
import FaqComponent from '@common/components/FaqComponent';
import userPersonas from '@common/constants/userPersonas';
import FaqConstants from '@rental-manager/constants/FaqConstants';
import { validateAllInputs } from '@rental-manager/pages/Onboarding/DueDatePage/validation';
import dueDates from '@rental-manager/pages/Onboarding/DueDatePage/dueDates.json';
import IreneOnboardingCSEvents from '@rental-manager/tracking/OnboardingClickStreamEvents';
import type { ZrmClickstreamListing, ZrmClickstreamUser } from '@zg-rentals/clickstream';

interface Props {
  paymentDayOfTheMonth?: any;
  onSubmit: ({ paymentDayOfTheMonth, rentTermsKey }: { paymentDayOfTheMonth: any; rentTermsKey: string }) => any;
  onClose: () => void;
  onCancel: () => void;
  onBack: () => void;
  trackValidationError?: (data: any) => any;
  rentTermsKey: string;
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
}

interface State {
  paymentDayOfTheMonth: any;
  showCaveat: boolean;
  error: string;
}

class EditDueDateModal extends React.Component<Props, State> {
  state = {
    paymentDayOfTheMonth: this.props.paymentDayOfTheMonth,
    showCaveat: false,
    error: '',
  };

  onChangeSelect = (e: React.SyntheticEvent<any>): void => {
    const selectedOption = e.currentTarget.options[e.currentTarget.selectedIndex];
    const paymentDayOfTheMonth = parseInt(selectedOption.getAttribute('value'), 10);
    const showCaveat = selectedOption.innerText?.endsWith('*');

    this.setState({
      showCaveat,
      paymentDayOfTheMonth,
      error: '',
    });
  };

  onSubmit = (): Promise<any> | Record<string, boolean> => {
    const { onSubmit, rentTermsKey, currentProperty, user } = this.props;
    const { paymentDayOfTheMonth } = this.state;
    return Promise.resolve().then(() => {
      if (!validateAllInputs(this)) {
        return { error: true };
      }
      // this tracking details will be replaced when we migrate dashboard events- ticket here- https://zillowgroup.atlassian.net/browse/TPAY-7005
      IreneOnboardingCSEvents.emitContinueFromDueDatePageEvent({ currentProperty, user });
      return onSubmit({
        paymentDayOfTheMonth,
        rentTermsKey,
      });
    });
  };

  onCancel = (): void => {
    this.props.onCancel();
  };

  onClose = (): void => {
    this.props.onClose();
  };

  renderStartDateOptions = (): React.ReactNode => {
    const defaultOption = (
      <option key={-1} disabled value="">
        Select one
      </option>
    );

    const options = dueDates.map((date, index) => {
      if (date === null) {
        return (
          <option key={index} disabled>
            ──────
          </option>
        );
      }
      const [text, value] = date;

      return (
        <option key={index} value={value}>
          {text}
        </option>
      );
    });

    return [defaultOption, ...options];
  };

  render(): React.ReactNode {
    const { showCaveat, paymentDayOfTheMonth, error } = this.state;
    const startDateOptions = this.renderStartDateOptions();

    return (
      <Modal
        persona={userPersonas.LANDLORD}
        header="Payment setup"
        onRequestClose={this.onClose}
        dataAutoTestId="landlord-payment-settings"
      >
        <Container>
          <Section>
            <Text type="h3">When should your tenant send their rent each month?</Text>
          </Section>
          <Section>
            <Label className="flexDisplay" data-auto-test-id="payment-end-date-label">
              <span>Monthly payment date</span>
            </Label>
            <Select
              data-id="monthlyPaymentDate"
              value={paymentDayOfTheMonth}
              error={error}
              onChange={this.onChangeSelect}
            >
              {startDateOptions}
            </Select>
          </Section>

          {showCaveat && (
            <Section>
              <Text type="body-small">
                For any month with fewer than {paymentDayOfTheMonth} days, the last day of the month will be the default
                payment date.
              </Text>
            </Section>
          )}

          <Section>
            <Text type="body-small">
              Your renter is charged when they send payment. The funds reach your account a few days later.
            </Text>
          </Section>

          <Section>
            <SpacerRow>
              <FaqComponent.Landlord faqs={FaqConstants.ONBOARDING_V2.DUE_DATE_PAGE} />
            </SpacerRow>
          </Section>
          <Section>
            <Button type="primary" onClick={this.onSubmit} full>
              Update
            </Button>
          </Section>
          <Section>
            <Button type="tertiary" full data-statekey="Cancel" onClick={this.onCancel}>
              Cancel
            </Button>
          </Section>
        </Container>
      </Modal>
    );
  }
}

export default EditDueDateModal;
